"use client";

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { Send, MessageSquare } from 'lucide-react';
import { useUserMessagesContext } from '@/contexts/UserMessagesContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { safeResponse } from '@/utils/supabaseHelpers';
import { useProfile } from '@/hooks/useProfile';
import { useAppStore } from '@/store';

interface UserMessagingProps {
  userId: string;
  userName: string;
  onClose: () => void;
  hideInternalHeader?: boolean;
}

const UserMessaging: React.FC<UserMessagingProps> = ({ 
  userId, 
  userName, 
  onClose,
  hideInternalHeader = false
}) => {
  const { sendMessage, markAsRead, messages } = useUserMessagesContext();
  const [messageText, setMessageText] = useState<string>('');
  const [recipientAvatar, setRecipientAvatar] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const { profile: currentUserProfile } = useProfile();
  const { user: appUser } = useAppStore();

  // Determine if the conversation is with the current user themselves
  const isSelfConversation = user && userId === user.id;

  // Fetch recipient avatar
  useEffect(() => {
    if (!userId) return;
    const fetchRecipientAvatar = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', userId)
          .single();
        
        if (error) throw error;
        
        const safeData = safeResponse(data);
        if (safeData.avatar_url) {
          setRecipientAvatar(safeData.avatar_url as string);
        }
      } catch (error) {
        // fallback: no avatar
      }
    };
    
    fetchRecipientAvatar();
  }, [userId]);

  // Filter the messages for the current conversation directly in the component
  const currentConversation = useMemo(() => {
    console.log('[UserMessaging] useMemo: Re-evaluating currentConversation. Total messages from context:', messages.length);
    if (!user) return [];
    const filtered = messages
      .filter(
        (msg) =>
          (msg.sender_id === user.id && msg.recipient_id === userId) ||
          (msg.sender_id === userId && msg.recipient_id === user.id)
      )
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    console.log('[UserMessaging] useMemo: Filtered conversation messages:', filtered.length);
    return filtered;
  }, [messages, userId, user]);

  // Effect for marking messages as read and scrolling to bottom
  useEffect(() => {
    console.log('[UserMessaging] useEffect (currentConversation): Effect triggered. Current conversation length:', currentConversation.length);
    if (!user) return;
    currentConversation
      .filter(msg => msg.recipient_id === user.id && !msg.read)
      .forEach(msg => markAsRead(msg.id));
      
    // Scroll to bottom
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [currentConversation, user, markAsRead]);

  const handleSend = async () => {
    if (!messageText.trim()) return;
    
    if (isSelfConversation) {
      toast({
        title: "Cannot send message to yourself",
        description: "You cannot send messages to yourself.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    try {
      await sendMessage(userId, messageText);
      setMessageText('');
    } catch (error) {
      toast({
        title: 'Error sending message',
        description: 'Your message could not be sent. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // --- FIX: Get current user's avatar from both profile and appUser ---
  const getCurrentUserAvatar = () => {
    // Try currentUserProfile.avatar_url (from useProfile)
    if (currentUserProfile?.avatar_url) return currentUserProfile.avatar_url;
    // Try appUser.avatar (from global store)
    if (appUser?.avatar) return appUser.avatar;
    // Try currentUserProfile.avatar (if present)
    if ((currentUserProfile as any)?.avatar) return (currentUserProfile as any).avatar;
    return null;
  };

  return (
    <Card className="w-full h-full flex flex-col rounded-none border-none shadow-none">
      {!hideInternalHeader && (
        <CardHeader className="py-3 border-b flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Avatar>
                {recipientAvatar ? (
                  <AvatarImage src={recipientAvatar} alt={userName} />
                ) : (
                  <AvatarFallback>{getInitials(userName)}</AvatarFallback>
                )}
              </Avatar>
              <CardTitle className="text-md">{userName}</CardTitle>
            </div>
          </div>
        </CardHeader>
      )}
      
      <CardContent className="p-0 flex-grow overflow-hidden">
        <ScrollArea className="h-full p-4">
          {currentConversation.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mb-2 opacity-20" />
              <p>No messages yet</p>
              <p className="text-sm">Start the conversation by sending a message below.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentConversation.map((message) => {
                const isOutgoing = message.sender_id === user?.id;
                return (
                  <div 
                    key={message.id} 
                    className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isOutgoing && (
                      <Avatar className="mr-2 h-8 w-8">
                        {recipientAvatar ? (
                          <AvatarImage src={recipientAvatar} alt={userName} />
                        ) : (
                          <AvatarFallback>{getInitials(userName)}</AvatarFallback>
                        )}
                      </Avatar>
                    )}
                    <div 
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        isOutgoing 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted'
                      }`}
                    >
                      <div className="break-words">{message.content}</div>
                      <div 
                        className={`text-xs mt-1 ${
                          isOutgoing ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}
                      >
                        {format(new Date(message.created_at), 'p')}
                      </div>
                    </div>
                    {isOutgoing && (
                      <Avatar className="ml-2 h-8 w-8">
                        {getCurrentUserAvatar() ? (
                          <AvatarImage src={getCurrentUserAvatar()} alt={currentUserProfile?.full_name || currentUserProfile?.username || 'You'} />
                        ) : (
                          <AvatarFallback>{getInitials(currentUserProfile?.full_name || currentUserProfile?.username || appUser?.name)}</AvatarFallback>
                        )}
                      </Avatar>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>
      </CardContent>
      
      <CardFooter className="border-t pt-3 pb-3">
        <form 
          className="flex w-full gap-2" 
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
        >
          <Input
            value={messageText}
            onChange={(e) => e.target.value.length <= 500 && setMessageText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={isSelfConversation ? "You cannot message yourself." : "Type your message..."}
            className="flex-1"
            disabled={isLoading || isSelfConversation}
          />
          <Button type="submit" size="icon" disabled={isLoading || isSelfConversation}>
            <Send size={18} />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
};

export default UserMessaging;